import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sun, ArrowLeft, Calendar, Clock, Loader2 } from "lucide-react";
import { Seo, siteUrl } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import FeedbackLink from "@/components/FeedbackLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MD_COMPONENTS = {
  h1: ({ node, ...props }) => <h2 className="font-display text-2xl font-semibold mt-8" {...props} />,
  h2: ({ node, ...props }) => <h2 className="font-display text-2xl font-semibold mt-8" {...props} />,
  h3: ({ node, ...props }) => <h3 className="font-display text-xl font-semibold mt-6" {...props} />,
  p: ({ node, ...props }) => <p className="text-[17px] leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-2 text-[17px]" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-2 text-[17px]" {...props} />,
  a: ({ node, ...props }) => <a className="text-amber-600 hover:text-amber-700 underline underline-offset-2" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-stone-900" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-amber-300 pl-4 italic text-stone-600" {...props} />,
  code: ({ node, ...props }) => <code className="bg-stone-100 rounded px-1.5 py-0.5 text-sm font-mono" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined); // undefined=loading, null=not found
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/blog/posts/${slug}`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/blog/posts`).then((r) => r.data).catch(() => []),
    ]).then(([post, all]) => {
      if (cancelled) return;
      setArticle(post);
      setRelated((all || []).filter((a) => a.slug !== slug).slice(0, 2));
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (article === undefined) {
    return (
      <div className="min-h-screen grid place-items-center text-stone-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!article) return <Navigate to="/blog" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.description,
    image: article.hero,
    datePublished: article.date,
    dateModified: article.date,
    author: { "@type": "Organization", name: "HomeSchool Hub" },
    publisher: {
      "@type": "Organization",
      name: "HomeSchool Hub",
      logo: { "@type": "ImageObject", url: `${siteUrl}/logo192.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${siteUrl}/blog/${article.slug}` },
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900" data-testid="blog-post-page">
      <Seo
        title={article.title}
        description={article.description}
        path={`/blog/${article.slug}`}
        image={article.hero}
        jsonLd={jsonLd}
        type="article"
      />

      <header className="sticky top-0 z-40 glass border-b border-stone-200/60">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg" data-testid="post-brand-link">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-amber-500 text-white">
              <Sun className="w-5 h-5" />
            </span>
            HomeSchool Hub
          </Link>
          <Link to="/blog" className="text-sm text-stone-600 hover:text-stone-900 flex items-center gap-1.5" data-testid="post-back-to-blog">
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <div className="flex items-center gap-2 flex-wrap">
          {(article.tags || []).map((t) => (
            <span key={t} className="rounded-full bg-amber-50 text-amber-800 text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 border border-amber-100">{t}</span>
          ))}
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15]" data-testid="post-title">
          {article.title}
        </h1>
        <p className="mt-4 text-stone-600 text-lg leading-relaxed">{article.description}</p>
        <div className="mt-6 flex items-center gap-4 text-sm text-stone-500">
          {article.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(article.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>}
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{article.read_mins} min read</span>
        </div>

        {article.hero && <img src={article.hero} alt={article.title} className="mt-10 rounded-3xl w-full h-72 object-cover" loading="lazy" />}

        <article className="mt-10 space-y-5 text-stone-800 leading-relaxed" data-testid="post-body">
          <ReactMarkdown components={MD_COMPONENTS}>{article.body_markdown || ""}</ReactMarkdown>
        </article>

        <div className="mt-16 rounded-3xl bg-gradient-to-br from-amber-500 to-orange-500 text-white p-8 md:p-10 grain">
          <h3 className="font-display text-2xl font-bold">Get compliance out of your inbox.</h3>
          <p className="mt-2 text-white/90">HomeSchool Hub automates the portfolio, attendance log, and evaluator packet Florida requires.</p>
          <Link to="/register" data-testid="post-cta-register">
            <Button className="mt-6 rounded-full bg-white text-amber-600 hover:bg-stone-100 px-6 py-5 font-semibold">
              Try HomeSchool Hub free
            </Button>
          </Link>
        </div>

        {related.length > 0 && (
          <section className="mt-16" aria-labelledby="related-heading">
            <h2 id="related-heading" className="font-display text-2xl font-semibold">Keep reading</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {related.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-sm transition-all" data-testid={`related-${a.slug}`}>
                  {a.hero && <img src={a.hero} alt={a.title} className="w-full h-32 object-cover" loading="lazy" />}
                  <div className="p-4">
                    <div className="font-display font-semibold leading-snug">{a.title}</div>
                    <div className="mt-2 text-xs text-stone-500">{a.read_mins} min read</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-stone-200 py-8 text-center text-sm text-stone-500">
        <div className="mx-auto max-w-3xl px-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <span>© {new Date().getFullYear()} HomeSchool Hub — Made for Florida homeschool families.</span>
          <FeedbackLink variant="muted" label="Send feedback" />
        </div>
      </footer>
    </div>
  );
}
